// Articles (LISTENING.md "Reader mapping"): the words of the non-skipped source paragraphs, in order, are the parts'
// words in order (a part with n words takes the next n). Null when the counts don't match (legacy data).
import { chunks, sourceParagraphs, type Article } from "../model/article";
import { SENTENCE, strength, wordsOf, type Word } from "./wordTiming";

export interface WordSpot { paragraph: number; start: number; end: number }

export interface NarrationLayout {
  parts: { text: string; words: Word[]; located: (WordSpot | null)[] }[];
  /** paragraph → sorted (start, part, word). */
  starts: Map<number, [number, number, number][]>;
}

export function layoutFor(a: Article): NarrationLayout | null {
  const paragraphs = sourceParagraphs(a);
  const parts = chunks(a);
  if (!paragraphs.length || !parts.length) return null;
  const skipped = new Set(a.meta.skipped ?? []);
  const pw = paragraphs.map((p) => wordsOf(p));
  const narrated: WordSpot[] = [];
  pw.forEach((ws, p) => { if (!skipped.has(p)) for (const w of ws) narrated.push({ paragraph: p, start: w[0], end: w[1] }); });
  const partWords = parts.map((t) => wordsOf(t));
  if (partWords.reduce((n, w) => n + w.length, 0) !== narrated.length) return null;
  let g = 0;
  const out = parts.map((text, i) => ({ text, words: partWords[i], located: partWords[i].map(() => narrated[g++] ?? null) }));
  const starts = new Map<number, [number, number, number][]>();
  out.forEach((part, pi) => part.located.forEach((l, wi) => {
    if (!l) return;
    if (!starts.has(l.paragraph)) starts.set(l.paragraph, []);
    starts.get(l.paragraph)!.push([l.start, pi, wi]);
  }));
  for (const list of starts.values()) list.sort((x, y) => x[0] - y[0]);
  return { parts: out, starts };
}

/** The narrated word at or just before a character of a paragraph. */
export function wordAtOffset(l: NarrationLayout, paragraph: number, offset: number): { part: number; word: number } | null {
  const list = l.starts.get(paragraph);
  if (!list?.length) return null;
  let lo = 0, hi = list.length - 1, found = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (list[mid][0] <= offset) { found = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return { part: list[found][1], word: list[found][2] };
}

/** The sentence around word w of a part, as word indexes. */
export function sentenceAround(part: { text: string; words: Word[] }, w: number): [number, number] {
  const ws = part.words;
  const ends = (k: number) => k === ws.length - 1 || strength(part.text, ws[k], ws[k + 1][0]) === SENTENCE;
  let a = w, b = w;
  while (a > 0 && !ends(a - 1)) a--;
  while (b < ws.length - 1 && !ends(b)) b++;
  return [a, b];
}
